import React, { useRef } from 'react';
import { Box, Typography } from '@mui/material';

type PillTabSize = 'default' | 'large';

const INTERACTIVE_TARGET_SELECTOR =
  'button, a, input, select, textarea, [role="button"], [role="tab"], [role="textbox"], [contenteditable="true"], [aria-disabled="true"]';

type SizeTokens = {
  height: number;
  px: string;
  gap: string;
  fontSize: number;
  borderRadius: string;
};

const SIZE_TOKENS: Record<PillTabSize, SizeTokens> = {
  default: {
    height: 22,
    px: '8px',
    gap: '2px',
    fontSize: 11,
    borderRadius: '5px',
  },
  large: {
    height: 27,
    px: '10px',
    gap: '4px',
    fontSize: 13,
    borderRadius: '6px',
  },
};

type PillTabsProps<T extends string> = {
  tabs: readonly T[];
  /** A single tab, or an array for independent toggle buttons. */
  value: T | readonly T[];
  onChange: (tab: T) => void;
  onDoubleClick?: (tab: T) => void;
  size?: PillTabSize;
  fontSize?: number;
  /** Override label weight in both active and inactive states. */
  fontWeight?: number;
  /** Override display labels for specific tabs. Falls back to capitalized tab value. */
  labels?: Partial<Record<T, React.ReactNode>>;
  /** Use a non-native button when a rich label contains an inline editor. */
  labelComponent?: 'button' | 'span';
  /** Wrap a tab button, for example with a tooltip, without changing its layout. */
  wrapTab?: (tab: T, button: React.ReactElement) => React.ReactNode;
  /** Content sharing the tab's active background, outside the button itself. */
  groupContent?: (tab: T) => React.ReactNode;
  /** Full-height, square groups sized to their content instead of small pills. */
  groupVariant?: 'pill' | 'fill';
  /** Also activate the tab from empty space around its label and group content. */
  groupClickable?: boolean;
  /** Wrap tabs onto the next row instead of overflowing the parent. */
  wrap?: boolean;
  /** Extra content in the same flex row (e.g. an add button after the last tab). */
  children?: React.ReactNode;
};

/**
 * Compact tab row. Pass {@link PillTabsProps.wrap} when the parent can clip
 * overflow (e.g. a side panel) so tabs move onto the next line.
 */
export const PillTabs = <T extends string>({
  tabs,
  value,
  onChange,
  onDoubleClick,
  size = 'default',
  fontSize,
  fontWeight,
  labels,
  labelComponent = 'button',
  wrapTab,
  groupContent,
  groupVariant = 'pill',
  groupClickable = false,
  wrap = false,
  children,
}: PillTabsProps<T>) => {
  const tokens = SIZE_TOKENS[size];
  const fillGroups = groupVariant === 'fill';
  const isToggleGroup = typeof value !== 'string';
  const selectedIndex = tabs.findIndex((tab) => tab === value);
  const suppressClickForDoubleClick = useRef<T | undefined>(undefined);

  return (
    <Box
      role={isToggleGroup ? 'group' : 'tablist'}
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: wrap ? 'wrap' : 'nowrap',
        gap: fillGroups ? '4px' : tokens.gap,
        height: wrap ? 'auto' : '100%',
        width: fillGroups ? 'max-content' : undefined,
        maxWidth: wrap ? '100%' : undefined,
        minWidth: wrap ? 0 : undefined,
      }}
    >
      {tabs.map((tab, index) => {
        const isSelected =
          typeof value === 'string' ? tab === value : value.includes(tab);
        const tabButton = (
          <Box
            component={labelComponent}
            type={labelComponent === 'button' ? 'button' : undefined}
            role={isToggleGroup ? 'button' : 'tab'}
            aria-selected={isToggleGroup ? undefined : isSelected}
            aria-pressed={isToggleGroup ? isSelected : undefined}
            tabIndex={isToggleGroup || isSelected ? 0 : -1}
            onClick={(event: React.MouseEvent<HTMLElement>) => {
              if (suppressClickForDoubleClick.current === tab) {
                suppressClickForDoubleClick.current = undefined;
                event.stopPropagation();
                return;
              }
              onChange(tab);
            }}
            onMouseDown={
              onDoubleClick
                ? (event: React.MouseEvent<HTMLElement>) => {
                    if (event.detail !== 2) return;
                    if (
                      event.target instanceof Element &&
                      event.target.closest(INTERACTIVE_TARGET_SELECTOR) !==
                        event.currentTarget
                    )
                      return;
                    event.preventDefault();
                    event.stopPropagation();
                    suppressClickForDoubleClick.current = tab;
                    onDoubleClick(tab);
                  }
                : undefined
            }
            onKeyDown={(event: React.KeyboardEvent<HTMLElement>) => {
              if (event.target !== event.currentTarget) return;
              if (
                labelComponent !== 'button' &&
                (event.key === 'Enter' || event.key === ' ')
              ) {
                event.preventDefault();
                onChange(tab);
                return;
              }
              if (isToggleGroup || !tabs.length) return;
              const currentIndex = selectedIndex >= 0 ? selectedIndex : index;
              const getTab = (tabIndex: number): T => tabs[tabIndex] ?? tab;
              if (event.key === 'ArrowRight') {
                event.preventDefault();
                onChange(getTab((currentIndex + 1) % tabs.length));
              }
              if (event.key === 'ArrowLeft') {
                event.preventDefault();
                onChange(
                  getTab((currentIndex - 1 + tabs.length) % tabs.length),
                );
              }
              if (event.key === 'Home') {
                event.preventDefault();
                onChange(getTab(0));
              }
              if (event.key === 'End') {
                event.preventDefault();
                onChange(getTab(tabs.length - 1));
              }
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: tokens.height,
              px: tokens.px,
              flexShrink: 0,
              appearance: 'none',
              WebkitAppearance: 'none',
              background: 'transparent',
              border: 'none',
              backgroundColor:
                isSelected && !groupContent ? 'rgba(0,0,0,0.05)' : undefined,
              borderRadius: fillGroups ? 0 : tokens.borderRadius,
              boxSizing: 'border-box',
              cursor: 'pointer',
              margin: 0,
              outline: 'none',
              font: 'inherit',
              color: 'inherit',

              '&:hover': {
                backgroundColor: groupContent
                  ? 'transparent'
                  : isSelected
                    ? 'rgba(0,0,0,0.05)'
                    : 'rgba(0,0,0,0.03)',
              },

              '&:focus-visible': {
                boxShadow: '0 0 0 1px rgba(0,0,0,0.25)',
              },
            }}
          >
            <Typography
              component="span"
              data-pill-tab-selected={isSelected || undefined}
              sx={{
                fontSize: fontSize ?? tokens.fontSize,
                fontWeight: fontWeight ?? (isSelected ? 600 : 500),
                letterSpacing: '0.04em',
                lineHeight: 1,
                whiteSpace: 'nowrap',
                color: isSelected ? 'rgba(0,0,0,0.8)' : 'rgba(0,0,0,0.5)',
              }}
            >
              {labels?.[tab] ?? tab.charAt(0).toUpperCase() + tab.slice(1)}
            </Typography>
          </Box>
        );
        return (
          <React.Fragment key={tab}>
            {groupContent ? (
              <Box
                onClick={
                  groupClickable
                    ? (event) => {
                        if (suppressClickForDoubleClick.current === tab) {
                          suppressClickForDoubleClick.current = undefined;
                          event.stopPropagation();
                          return;
                        }
                        // Nested controls keep their own actions, including the label.
                        if (
                          event.target instanceof Element &&
                          event.target.closest(INTERACTIVE_TARGET_SELECTOR)
                        )
                          return;
                        onChange(tab);
                      }
                    : undefined
                }
                onMouseDown={
                  groupClickable && onDoubleClick
                    ? (event) => {
                        if (event.detail !== 2) return;
                        if (
                          event.target instanceof Element &&
                          event.target.closest(INTERACTIVE_TARGET_SELECTOR)
                        )
                          return;
                        event.preventDefault();
                        event.stopPropagation();
                        suppressClickForDoubleClick.current = tab;
                        onDoubleClick(tab);
                      }
                    : undefined
                }
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: tokens.gap,
                  height: fillGroups ? tokens.height + 8 : tokens.height,
                  pl: fillGroups ? '4px' : undefined,
                  pr: '4px',
                  flexShrink: 0,
                  cursor: groupClickable ? 'pointer' : undefined,
                  userSelect: groupClickable ? 'none' : undefined,
                  borderRadius: fillGroups ? '6px' : tokens.borderRadius,
                  backgroundColor: isSelected
                    ? 'rgba(0,0,0,0.05)'
                    : 'transparent',
                  '&:hover': {
                    backgroundColor: isSelected
                      ? 'rgba(0,0,0,0.05)'
                      : 'rgba(0,0,0,0.03)',
                  },
                  '&:hover [data-pill-tab-selected="true"], &:hover [data-phase-selected="true"]':
                    {
                      color: 'rgba(0,0,0,1)',
                      opacity: 1,
                    },
                }}
              >
                {wrapTab ? wrapTab(tab, tabButton) : tabButton}
                {groupContent(tab)}
              </Box>
            ) : wrapTab ? (
              wrapTab(tab, tabButton)
            ) : (
              tabButton
            )}
          </React.Fragment>
        );
      })}
      {children}
    </Box>
  );
};
